import React, { useEffect, useMemo, useState, useRef } from "react";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Upload, Trash2, Save, FileText, Loader2 } from "lucide-react";

const DATA_PATHS = [
  { value: "", label: "— skip —" },
  { value: "client.full_name", label: "Client · Full Name" },
  { value: "client.first_name", label: "Client · First Name" },
  { value: "client.last_name", label: "Client · Last Name" },
  { value: "client.email", label: "Client · Email" },
  { value: "client.phone", label: "Client · Phone" },
  { value: "client.uci", label: "Client · UCI" },
  { value: "client.date_of_birth", label: "Client · Date of Birth" },
  { value: "client.address", label: "Client · Address" },
  { value: "application.application_number", label: "Case · Application #" },
  { value: "application.ircc_file_number", label: "Case · IRCC File #" },
  { value: "application.app_type_name", label: "Case · Application Type" },
  { value: "firm.name", label: "Firm (RCIC) · Full Name" },
  { value: "firm.first_name", label: "Firm (RCIC) · First Name" },
  { value: "firm.last_name", label: "Firm (RCIC) · Last Name" },
  { value: "firm.rcic_license", label: "Firm · CICC License #" },
  { value: "firm.email", label: "Firm · Email" },
  { value: "firm.phone", label: "Firm · Phone" },
  { value: "firm.phone_country_code", label: "Firm · Phone Country Code" },
  { value: "firm.fax", label: "Firm · Fax" },
  { value: "firm.fax_country_code", label: "Firm · Fax Country Code" },
  { value: "firm.unit", label: "Firm · Unit / Suite" },
  { value: "firm.street_no", label: "Firm · Street Number" },
  { value: "firm.street_name", label: "Firm · Street Name" },
  { value: "firm.city", label: "Firm · City" },
  { value: "firm.province", label: "Firm · Province / State" },
  { value: "firm.country", label: "Firm · Country" },
  { value: "firm.postal_code", label: "Firm · Postal Code" },
  { value: "firm.address", label: "Firm · Full Address" },
  { value: "const.today", label: "Constant · Today (YYYY-MM-DD)" },
  { value: "const.today_long", label: "Constant · Today (long date)" },
];

export default function ImmigrationIRCCForms() {
  const [forms, setForms] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [uploadName, setUploadName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [savingMap, setSavingMap] = useState(false);
  const [draftMap, setDraftMap] = useState({});
  const fileRef = useRef();

  const load = async () => {
    const r = await api.get("/immigration/ircc-forms");
    setForms(r.data || []);
  };
  useEffect(() => { load(); }, []);

  const selected = useMemo(() => forms.find(f => f.id === selectedId), [forms, selectedId]);
  useEffect(() => { if (selected) setDraftMap({ ...(selected.field_mapping || {}) }); }, [selected]);

  const uploadForm = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) return toast.error("Pick a PDF first");
    if (!uploadName.trim()) return toast.error("Give this form a name (e.g. IMM 5476)");
    const fd = new FormData(); fd.append("file", f);
    setUploading(true);
    try {
      const r = await api.post(`/immigration/ircc-forms/upload?name=${encodeURIComponent(uploadName.trim())}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded ${r.data.name} with ${r.data.field_names.length} fields`);
      setUploadName(""); fileRef.current.value = "";
      await load();
      setSelectedId(r.data.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const saveMap = async () => {
    if (!selected) return;
    setSavingMap(true);
    try {
      await api.patch(`/immigration/ircc-forms/${selected.id}`, { field_mapping: draftMap });
      toast.success("Mapping saved");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSavingMap(false); }
  };

  const removeForm = async () => {
    if (!selected) return;
    if (!window.confirm(`Delete form "${selected.name}"? Cases can no longer fill it.`)) return;
    try { await api.delete(`/immigration/ircc-forms/${selected.id}`); toast.success("Deleted"); setSelectedId(""); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div>
        <h1 className="heading-font text-3xl font-bold text-slate-900">IRCC Forms</h1>
        <p className="text-sm text-slate-500 mt-1">
          Upload the official fillable IRCC PDFs (e.g. <span className="mono">IMM 5476</span> — Use of Representative). The system auto-detects AcroForm fields
          and lets you map each one to a case/client data path. From any case, officers can fill the form with one click.
        </p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-end gap-3 flex-wrap">
        <div className="flex-1 min-w-[220px]">
          <Label className="text-xs text-slate-600">Form Name</Label>
          <Input value={uploadName} onChange={e => setUploadName(e.target.value)} placeholder="e.g. IMM 5476 — Use of Representative" data-testid="ircc-upload-name"/>
        </div>
        <div className="flex-1 min-w-[200px]">
          <Label className="text-xs text-slate-600">Blank Fillable PDF</Label>
          <input ref={fileRef} type="file" accept=".pdf" className="block w-full text-sm" data-testid="ircc-upload-file"/>
        </div>
        <Button onClick={uploadForm} disabled={uploading} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="ircc-upload-btn">
          {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin"/> : <Upload className="w-4 h-4 mr-1"/>}Upload
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-6">
        <div className="bg-white rounded-xl border border-slate-200 p-3 h-fit">
          <div className="text-xs uppercase text-slate-500 px-2 pb-2">Uploaded Forms</div>
          <div className="space-y-1">
            {forms.length === 0 && <div className="text-sm text-slate-400 p-2">No forms uploaded yet.</div>}
            {forms.map(f => (
              <button
                key={f.id}
                onClick={() => setSelectedId(f.id)}
                data-testid={`ircc-form-${f.id}`}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center justify-between gap-2 ${
                  selectedId === f.id ? "bg-orange-50 text-orange-800 border border-orange-200" : "hover:bg-slate-50 border border-transparent"
                }`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <FileText className="w-4 h-4 shrink-0 text-slate-400"/>
                  <span className="font-medium truncate">{f.name}</span>
                </div>
                <span className="text-[10px] text-slate-500 shrink-0">{f.field_names?.length || 0} fields</span>
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
          {!selected ? (
            <div className="text-sm text-slate-500 py-10 text-center">Upload a form or pick one on the left.</div>
          ) : (
            <>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-xs uppercase text-slate-500">Mapping · {selected.name}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{selected.original_filename} · {selected.field_names.length} AcroForm fields</div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={removeForm} className="text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="ircc-delete">
                    <Trash2 className="w-4 h-4 mr-1"/>Delete
                  </Button>
                  <Button onClick={saveMap} disabled={savingMap} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="ircc-save-map">
                    <Save className="w-4 h-4 mr-1"/>{savingMap ? "Saving…" : "Save Mapping"}
                  </Button>
                </div>
              </div>
              <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 max-h-[520px] overflow-auto">
                {selected.field_names.map((fname) => (
                  <div key={fname} className="flex items-center gap-3 p-3">
                    <div className="w-1/2 min-w-0">
                      <div className="mono text-xs text-slate-700 truncate" title={fname}>{fname}</div>
                    </div>
                    <select
                      className="flex-1 border border-slate-200 rounded-lg text-sm px-2 py-1.5"
                      value={draftMap[fname] || ""}
                      onChange={e => setDraftMap({ ...draftMap, [fname]: e.target.value })}
                      data-testid={`ircc-map-${fname}`}
                    >
                      {DATA_PATHS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                    </select>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500">
                Tip — a smart default is already assigned where field names match common patterns. Fine-tune, Save, and the form will auto-fill from each case.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
